import { useMediaQuery } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import useAttachments from "hooks/useAttachments";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { isEmpty } from "utils/general";

import {
    INVALID_DATE_TEXT,
    REQUISITION_SUBMIT_CONFIRM_MESSAGE,
} from "../../../constants/generalConstants";
import { MOBILE_MEDIA_QUERY } from "../../../constants/mediaQueries";
import { setPageNum } from "../../../redux/actions/createRequisitionActions";
import { setShowRequisitionDialogue } from "../../../redux/actions/generalActions";
import { deleteCart } from "../../../services/cart/cartAPIs";
import { postRequisition } from "../../../services/requisition/requisitionAPIs";
import { styledDialogChildSX } from "../../../styled/styledSXProperties/styledDialogueSX";
import { baseStyledPaperSX } from "../../../styled/styledSXProperties/styledPaperSX";
import alertDispatcher from "../../../utils/alertsUtils";
import {
    allReqLinesDifferent,
    closeCreateReqForm,
    removeTemporaryUniqueLineIds,
    someReqLinesAmended,
} from "../../../utils/createRequisitionUtils";
import { totalCost } from "../../../utils/shoppingCartUtils";
import CurrencyFigure from "../../displays/currencyFigure";
import DetailListPiped from "../../displays/detailListPiped";
import DetailListVertical from "../../displays/detailListVertical";
import YesNoPrompt from "../prompts/yesNoPrompt";
import AttachmentsReview from "./attachmentReview/attachmentsReview";
import HeaderForm from "./page1HeaderForm/headerForm";
import LineItemForm from "./page2LineItems/lineItemForm";
import Review from "./page3Review/review";
import RequisitionDialogActions from "./requisitionDialogActions";
import RequisitionDialogTitle from "./requisitionDialogTitle";

const splitSelectedAddressString = (str) => {
    if (str == undefined) {
        return [undefined, undefined];
    }
    const countries = [
        "england",
        "Scotland",
        "Northern Ireland",
        "wales",
        "ireland",
    ];
    const lwrStr = str.toLowerCase();
    for (const country of countries) {
        if (lwrStr.endsWith(country)) {
            return [str.slice(0, -country.length - 2), country];
        }
    }
    return [str, undefined];
};

const RequisitionDialogueContentDispatcher = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isDesktop = useMediaQuery(MOBILE_MEDIA_QUERY);

    const activeCart = useSelector((state) => state.general.activeCart);
    const isInternalReq = useSelector(
        (state) => state.createRequisition.isInternalReq
    );
    const pageNum = useSelector((state) => state.createRequisition.pageNum);
    const reqLines = useSelector(
        (state) => state.createRequisition.iBuyRequisition.requisitionLines
    );
    const reqHeader = useSelector(
        (state) => state.createRequisition.iBuyRequisition.requisitionHeaderForm
    );
    const req = useSelector((state) => state.createRequisition.iBuyRequisition);
    const isPostPoReq = useSelector(
        (state) => state.createRequisition.isPostPoReq
    );
    const postPOMetadata = useSelector(
        (state) => state.createRequisition.postPOMetadata
    );
    const validatedPages = useSelector(
        (state) => state.createRequisition.validatedPages
    );
    const isAmendedReq = useSelector(
        (state) => state.createRequisition.amendedReq
    );
    const requesterId = useSelector(
        (state) => state.createRequisition.iBuyRequisition.REQUESTER_ID
    );
    let requesteUserName = useSelector(
        (state) => state.createRequisition.iBuyRequisition.REQUESTER_USERID_NAME
    );
    const requesterName = requesteUserName
        ? requesteUserName.split("-")[1]
        : "";
    const punchOutCartIsActive = useSelector(
        (state) => state.punchOut.punchOutCartActive
    );

    const {
        attachments,
        headerAttachments,
        getAttachmentsByUniqueLineId,
        deleteAttachmentsOnFormClose,
        deleteStagedAttachments,
    } = useAttachments();

    // Local state
    const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(false);
    // Modal closing functions
    const [cancelPromptOpen, setCancelPromptOpen] = useState(false);
    const [submitPromptOpen, setSubmitPromptOpen] = useState(false);

    const closeForm = async () => {
        deleteAttachmentsOnFormClose();
        closeCreateReqForm(dispatch);
    };

    const totalValue = totalCost(reqLines);

    const isNullOrEmpty = (str) => {
        return !str || str.trim() === "";
    };

    const hasAttachmentsChanged = () => {
        let totalReqAttachments = 0;
        reqLines?.forEach((line) => {
            totalReqAttachments += line?.attachments?.length;
        });

        totalReqAttachments += reqHeader?.attachments?.length;

        return totalReqAttachments !== attachments?.length;
    };

    const handleComplete = () => {
        const newValidatedPages = validatedPages;
        newValidatedPages[pageNum] = false;

        const hasAttachmentsAmended = hasAttachmentsChanged();
        // If a post PO amendment, block submission if no changes made
        if (isPostPoReq && !someReqLinesAmended(postPOMetadata)) {
            alertDispatcher("postPOAmendmentSubmit", 400, dispatch);
            return;
        }

        // Prevent submission is duplicate requisition lines present in payload.
        if (!allReqLinesDifferent(req.requisitionLines)) {
            alertDispatcher("reqFormSubmit", 409, dispatch);
            return;
        }

        // Validate all required fields are present before submission. A user should not have to wait for the API calls to fail due to missing / invalid input data.

        // validate the need by date
        if (
            !isPostPoReq &&
            (isEmpty(reqHeader.NEED_BY_DATE) ||
                reqHeader.NEED_BY_DATE === INVALID_DATE_TEXT ||
                new Date(reqHeader.NEED_BY_DATE).getTime() < Date.now())
        ) {
            alertDispatcher("reqFormSubmit", 400001, dispatch);
            return;
        }
        // Check for invalid users if not a post po
        if (
            !isPostPoReq &&
            (isEmpty(req.REQUESTER_USERID_NAME) ||
                !!req.REQUESTER_USERID_NAME?.includes("- INACTIVE -"))
        ) {
            alertDispatcher("reqFormSubmit", 400002, dispatch);
            return;
        }

        // Check for empty values in the required fields
        // TODO: Activate this with a new PBI.

        // if (isEmpty(reqHeader.HEADER_DESCRIPTION)) {
        //     alertDispatcher(
        //         "reqFormSubmit",
        //         400003,
        //         dispatch,
        //         "header description"
        //     );
        //     return;
        // }

        // if (isEmpty(reqHeader.JUSTIFICATION)) {
        //     alertDispatcher(
        //         "reqFormSubmit",
        //         400003,
        //         dispatch,
        //         "header justification"
        //     );
        //     return;
        // }

        for (const line of req.requisitionLines) {
            if (!line?.CHANGE_ACTION && isAmendedReq) {
                line.CHANGE_ACTION = "UNCHANGED";
            }

            // Need by date for line items
            const needByDate = line?.NEED_BY_DATE?.toString();
            if (
                line.CHANGE_ACTION !== "UNCHANGED" &&
                (isEmpty(needByDate) ||
                    needByDate === INVALID_DATE_TEXT ||
                    new Date(needByDate).getTime() < Date.now())
            ) {
                alertDispatcher("reqFormSubmit", 400004, dispatch);
                return;
            }

            // Add final validations to line items
            // TODO: Activate this with a new PBI.
            // if (
            //     isEmpty(line.QUANTITY) ||
            //     isNaN(Number(line.QUANTITY)) ||
            //     Number(line.QUANTITY) <= 0
            // ) {
            //     // Add an alert item quantity has to be more than zero.
            //     return;
            // }

            // if (isEmpty(line.JUSTIFICATION)) {
            //     alertDispatcher(
            //         "reqFormSubmit",
            //         400003,
            //         dispatch,
            //         "item justification"
            //     );
            //     return;
            // }
        }

        // Strip fields not required in database. Autocomplete inputs such as delivery location
        // store non-id information at req line/header level for display purposes. Only IDs
        // should be sent to and displayed in Cosmos.
        const headerForm = req.requisitionHeaderForm;
        /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
        const {
            LOCATION_CODE,
            EXPENDITURE_ORGANIZATION_NAME,
            CONCATENATED_SEGMENTS,
            SEGMENT1_NAME,
            SEGMENT2_NAME,
            SEGMENT3_NAME,
            SEGMENT4_NAME,
            IS_VALIDATED,
            ...headerFormStripped
        } = headerForm;
        if (
            headerFormStripped.ADDRESS_TYPE === "otda" &&
            headerFormStripped.ONE_TIME_ADDRESS == undefined
        ) {
            const [address, country] = splitSelectedAddressString(
                headerFormStripped?.SELECTED_ONE_TIME_ADDRESS
            );
            headerFormStripped.ONE_TIME_ADDRESS = address;
            headerFormStripped.ONE_TIME_ADDRESS_COUNTRY = country;
            delete headerFormStripped.SELECTED_ONE_TIME_ADDRESS;
        }

        if ("SAVE_ADDRESS_FOR_FUTURE" in headerFormStripped) {
            delete headerFormStripped.SAVE_ADDRESS_FOR_FUTURE;
        }

        /*eslint no-unused-vars: ["error", { "ignoreRestSiblings": true }]*/
        const linesStripped = req.requisitionLines.map((line) => {
            const {
                LOCATION_CODE,
                EXPENDITURE_ORGANIZATION_NAME,
                CONCATENATED_SEGMENTS,
                SEGMENT1_NAME,
                SEGMENT2_NAME,
                SEGMENT3_NAME,
                SEGMENT4_NAME,
                status,
                statusCode,
                IS_VALIDATED,
                AUTOSOURCE_DOC_HEADER_ID,
                AUTOSOURCE_DOC_LINE_NUM,
                ...lineStripped
            } = line;

            if (
                Object.prototype.hasOwnProperty.call(
                    line,
                    "SMARTFORM_TEMPLATE_ID"
                ) &&
                line?.SMARTFORM_TEMPLATE_ID
            ) {
                line.SMART_FORM_TEMPLATE_ID = line?.SMARTFORM_TEMPLATE_ID;
            }
            if (
                Object.prototype.hasOwnProperty.call(line, "SMART_FORM_ID") &&
                line?.SMART_FORM_ID
            ) {
                line.SMART_FORM_TEMPLATE_ID = line?.SMART_FORM_ID;
            }

            if (
                lineStripped.ADDRESS_TYPE === "otda" &&
                lineStripped.ONE_TIME_ADDRESS == undefined
            ) {
                const [address, country] = splitSelectedAddressString(
                    lineStripped?.SELECTED_ONE_TIME_ADDRESS
                );

                lineStripped.ONE_TIME_ADDRESS = address;
                lineStripped.ONE_TIME_ADDRESS_COUNTRY = country;

                delete lineStripped.SELECTED_ONE_TIME_ADDRESS;
            }

            if ("SAVE_ADDRESS_FOR_FUTURE" in lineStripped) {
                delete lineStripped.SAVE_ADDRESS_FOR_FUTURE;
            }

            //added this to avoid null check CPA error
            for (let lineObjItem in line) {
                if (line[lineObjItem] === "None") {
                    delete line[lineObjItem];
                }
            }

            return line;
        });

        //TO//DO
        // Transform attachments as per recipient data-
        // we need to duplicate the attachments as per the number of recipients selected
        // let mod = headerFormStripped?.attachments;
        // headerFormStripped?.attachments?.forEach((attachment) => {
        //     attachment?.recipients?.forEach((rec) => {
        //         mod.push(attachment);
        //         mod.attachment.receipient = rec;
        //     });
        //     mod.pop(attachment);
        // });
        // headerFormStripped.attachments = mod;

        // Transforming create requisition payload to match expected database payload-

        const transformAttachment = (attachment) => {
            return {
                LEVEL_TYPE: attachment.LEVEL_TYPE,
                DATA_TYPE: "File",
                TITLE: attachment.TITLE,
                DESCRIPTION: !isNullOrEmpty(attachment.DESCRIPTION)
                    ? attachment.DESCRIPTION
                    : attachment.ATT_FILENAME, // If the description is empty, it will be replaced with scanId.file extension
                FILENAME: attachment.FILENAME,
                ATTACHMENT_TEXT: attachment.ATTACHMENT_TEXT,
                ATT_FILENAME: attachment.ATT_FILENAME, // This is being used to frame blob url in backend
                ATTACHMENT_ID: attachment.ATTACHMENT_ID,
                ATTACHMENT_LINE: attachment.ATTACHMENT_LINE,
                CREATED_DATE: attachment.CREATED_DATE,
                CATEGORY: attachment.CATEGORY,
                FILE_SIZE: attachment.FILE_SIZE,
            };
        };

        //transforming header attachments
        headerFormStripped.attachments = headerAttachments?.map((attachment) =>
            transformAttachment(attachment)
        );

        //transforming Line attachments
        linesStripped.forEach((line) => {
            const lineAttachments = getAttachmentsByUniqueLineId(
                line.uniqueLineId
            );
            line.attachments = lineAttachments?.map((attachment) =>
                transformAttachment(attachment)
            );
        });

        const post = {
            requisitionLines: linesStripped,
            requisitionHeaderForm: headerFormStripped,
            approvalsRef: [],
            supplierName: req.supplierName,
            id: req.id,
            REQUESTER_ID: requesterId,
        };

        if (isPostPoReq) {
            // if post po we have decided to strip all fields that aren't directly allowed by amendments
            post.requisitionLines = post.requisitionLines.map((line) => {
                const amendedReqLine = {
                    QUANTITY: parseFloat(line?.QUANTITY),
                    NEED_BY_DATE: line?.NEED_BY_DATE,
                    LINE_ATTRIBUTE5: line?.LINE_ATTRIBUTE5,
                    HARMONY_REQUISITION_LINE_ID:
                        line?.HARMONY_REQUISITION_LINE_ID,
                };

                if (hasAttachmentsAmended) {
                    amendedReqLine.attachments = line.attachments;
                }
                return amendedReqLine;
            });
        }

        post.requisitionLines = removeTemporaryUniqueLineIds(
            post.requisitionLines
        );

        postRequisition(
            post,
            dispatch,
            activeCart,
            navigate,
            newValidatedPages,
            isAmendedReq,
            deleteStagedAttachments,
            punchOutCartIsActive,
            deleteCart
        );
    };

    // Create form validator for line item form. Note - should you wish
    // to add a validator for the header form, this should be done via
    // a separate call to the useForm hook (there should be one validator
    // per form page).
    const {
        register,
        formState: { errors },
        handleSubmit,
    } = useForm();

    const isCpaReq =
        !!reqLines[0]?.CPA_NUMBER && !!(reqLines[0].CPA_NUMBER !== "None");

    const requisitionDetails = [
        {
            header: "Operating Unit",
            content: req.requisitionHeaderForm.ORG_NAME
                ? req.requisitionHeaderForm.ORG_NAME
                : "Not available",
            hasError: !req.requisitionHeaderForm.ORG_NAME,
        },
        {
            header: isCpaReq ? "Contract Number" : "Supplier",
            content: isCpaReq
                ? reqLines[0]?.CPA_NUMBER || "Not Specified"
                : req.supplierName || "Not Specified",
        },
        {
            header: "Total",
            content: totalValue ? (
                <CurrencyFigure
                    value={totalValue}
                    code={reqLines[0]?.CURRENCY_CODE}
                />
            ) : (
                "Invalid Input"
            ),
            hasError: !totalValue,
        },
        {
            header: "Sourcing Mode",
            content: isInternalReq ? "Internal" : "Purchase",
        },
        {
            header: "Requester",
            content: requesterName ? requesterName : "",
        },
    ];

    return (
        <>
            <RequisitionDialogTitle setConfirmOpen={setCancelPromptOpen} />
            <DialogContent sx={styledDialogChildSX}>
                <Grid container gap={2}>
                    <Grid item xs={12}>
                        {isDesktop ? (
                            <DetailListPiped details={requisitionDetails} />
                        ) : (
                            <DetailListVertical
                                condensed={true}
                                details={requisitionDetails}
                            />
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        {pageNum == 0 && (
                            <HeaderForm
                                setIsNextButtonDisabled={
                                    setIsNextButtonDisabled
                                }
                            />
                        )}
                        {pageNum == 1 && (
                            <LineItemForm
                                errors={errors}
                                register={register}
                                setIsNextButtonDisabled={
                                    setIsNextButtonDisabled
                                }
                            />
                        )}
                        {pageNum == 2 && (
                            <AttachmentsReview
                                setIsNextButtonDisabled={
                                    setIsNextButtonDisabled
                                }
                            />
                        )}
                        {pageNum == 3 && (
                            <Review reqHeader={reqHeader} reqLines={reqLines} />
                        )}
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions sx={styledDialogChildSX}>
                <RequisitionDialogActions
                    lineFormHandleSubmit={handleSubmit}
                    isNextButtonDisabled={isNextButtonDisabled}
                    setSubmitConfirmOpen={setSubmitPromptOpen}
                    handleComplete={handleComplete}
                />
            </DialogActions>

            <YesNoPrompt
                title="Close requisition form?"
                subtitle="Closing this form prior to submission will result in all entered requisition data being lost. Are you sure you wish to close this form?"
                promptOpen={cancelPromptOpen}
                setPromptOpen={setCancelPromptOpen}
                onClickYes={closeForm}
            />

            <YesNoPrompt
                title="Submit Requisition?"
                subtitle={REQUISITION_SUBMIT_CONFIRM_MESSAGE}
                promptOpen={submitPromptOpen}
                setPromptOpen={setSubmitPromptOpen}
                onClickYes={handleComplete}
            />
        </>
    );
};

const RequisitionDialog = () => {
    // Collect states and state dispatchers
    const isOpen = useSelector(
        (state) => state.general.showRequisitionDialogue
    );
    const darkMode = useSelector((state) => state.general.darkMode);
    const dispatch = useDispatch();
    const isDesktop = useMediaQuery(MOBILE_MEDIA_QUERY);

    const close = () => {
        dispatch(setPageNum(0));
        dispatch(setShowRequisitionDialogue(false));
    };

    const handleClose = (e_, reason) => {
        if (reason && reason == "backdropClick") return;

        close();
    };

    return (
        <Dialog
            fullWidth
            maxWidth="xl"
            onClose={handleClose}
            open={isOpen}
            fullScreen={!isDesktop}
            PaperProps={{ sx: baseStyledPaperSX(darkMode) }}
        >
            <RequisitionDialogueContentDispatcher />
        </Dialog>
    );
};

export default RequisitionDialog;
